import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  LinearProgress,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tab,
  Tabs,
  Alert,
  Divider,
  useTheme,
  Fade,
  Zoom,
  Slide,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  OpenInNew as OpenIcon,
  MarkEmailRead as ReadIcon,
  MouseOutlined as ClickIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoAwesomeIcon,
  BarChart as BarChartIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import EmailTemplates from './EmailTemplates';

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  from_name?: string;
  from_email?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled';
  total_recipients: number;
  total_sent: number;
  total_delivered: number;
  total_opened: number;
  total_clicked: number;
  total_bounced: number;
  total_failed: number;
  created_at: string;
  scheduled_at?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  subject: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function EmailCampaigns() {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [crmStages, setCrmStages] = useState<any[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [recipientType, setRecipientType] = useState<'stage' | 'individual' | 'list'>('stage');
  const [individualEmail, setIndividualEmail] = useState<string>('');
  const [individualName, setIndividualName] = useState<string>('');
  const [emailList, setEmailList] = useState<Array<{email: string, name: string}>>([{email: '', name: ''}]);
  const [bulkEmailInput, setBulkEmailInput] = useState<string>('');
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    from_name: '',
    filters: [{ filter_type: 'crm_stage', filter_value: '' }],
  });
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null);
  const [showCampaignDetails, setShowCampaignDetails] = useState(false);
  const [startingCampaign, setStartingCampaign] = useState<string | null>(null);

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadCrmStages();
  }, []);

  const loadCrmStages = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/crm/pipelines`);
      const allStages: any[] = [];
      response.data.data?.forEach((pipeline: any) => {
        pipeline.stages?.forEach((stage: any) => {
          allStages.push({
            id: stage.id,
            name: stage.name,
            pipelineName: pipeline.name
          });
        });
      });
      setCrmStages(allStages);
    } catch (error) {
      console.error('Erro ao carregar estágios do CRM:', error);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/email-campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/email/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const handleCreateCampaign = async () => {
    if (!selectedTemplate || !newCampaign.name || !newCampaign.subject) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    // Validar baseado no tipo de destinatário
    if (recipientType === 'stage' && !selectedStageId) {
      alert('Por favor, selecione um estágio do CRM');
      return;
    }

    if (recipientType === 'individual' && !individualEmail) {
      alert('Por favor, informe o email do destinatário');
      return;
    }

    if (recipientType === 'list') {
      const validEmails = emailList.filter(item => item.email && item.email.trim() !== '');
      if (validEmails.length === 0) {
        alert('Por favor, adicione pelo menos um email válido');
        return;
      }
      // Validar formato de todos os emails
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (const item of validEmails) {
        if (!emailRegex.test(item.email)) {
          alert(`Por favor, informe um email válido: ${item.email}`);
          return;
        }
      }
    }

    // Validar formato do email se for individual
    if (recipientType === 'individual') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(individualEmail)) {
        alert('Por favor, informe um email válido');
        return;
      }
    }

    try {
      if (selectedTemplate) {
        let recipients: any[] = [];
        let filters: any[] = [];

        if (recipientType === 'stage') {
          // Enviar para estágio do CRM
          filters = [{ filter_type: 'crm_stage', filter_value: selectedStageId }];
        } else if (recipientType === 'individual') {
          // Enviar para email individual
          recipients = [{
            type: 'manual',
            email: individualEmail,
            name: individualName || individualEmail
          }];
        } else if (recipientType === 'list') {
          // Enviar para lista de emails
          recipients = emailList
            .filter(item => item.email && item.email.trim() !== '')
            .map(item => ({
              type: 'manual',
              email: item.email.trim(),
              name: item.name.trim() || item.email.trim()
            }));
        }

        // Se estiver editando, atualizar a campanha
        if (selectedCampaign) {
          await axios.put(`${getApiUrl()}/email-campaigns/${selectedCampaign.id}`, {
            name: newCampaign.name,
            subject: newCampaign.subject,
            from_name: newCampaign.from_name,
          });
        } else {
          // Criar do template
          await axios.post(`${getApiUrl()}/email-campaigns/from-template`, {
            template_id: selectedTemplate,
            name: newCampaign.name,
            subject: newCampaign.subject,
            from_name: newCampaign.from_name,
            filters: filters,
            recipients: recipients,
            created_by: localStorage.getItem('userId') || '',
          });
        }
      }
      
      loadCampaigns();
      
      // Resetar form
      setNewCampaign({
        name: '',
        subject: '',
        from_name: '',
        filters: [{ filter_type: 'crm_stage', filter_value: '' }],
      });
      setSelectedTemplate('');
      setSelectedStageId('');
      setRecipientType('stage');
      setIndividualEmail('');
      setIndividualName('');
      setEmailList([{email: '', name: ''}]);
      setBulkEmailInput('');
      setSelectedCampaign(null);
      setTabValue(0); // Voltar para primeira aba
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      alert('Erro ao criar campanha');
    }
  };

  const handleStartCampaign = async (campaignId: string) => {
    console.log('🚀 Iniciando campanha:', campaignId);
    
    if (startingCampaign) {
      console.log('⚠️ Já há uma campanha sendo iniciada');
      return;
    }

    setStartingCampaign(campaignId);
    
    try {
      console.log('📡 Enviando requisição para:', `${getApiUrl()}/email-campaigns/${campaignId}/start`);
      const response = await axios.post(`${getApiUrl()}/email-campaigns/${campaignId}/start`);
      console.log('✅ Resposta recebida:', response.data);
      alert(response.data.message || 'Campanha iniciada com sucesso!');
      await loadCampaigns();
    } catch (error: any) {
      console.error('❌ Erro ao iniciar campanha:', error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Erro ao iniciar campanha';
      alert(`Erro ao iniciar campanha: ${errorMessage}`);
    } finally {
      setStartingCampaign(null);
    }
  };

  const handlePauseCampaign = async (campaignId: string) => {
    try {
      await axios.post(`${getApiUrl()}/email-campaigns/${campaignId}/pause`);
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao pausar campanha:', error);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta campanha?')) {
      try {
        await axios.delete(`${getApiUrl()}/email-campaigns/${campaignId}`);
        loadCampaigns();
      } catch (error) {
        console.error('Erro ao excluir campanha:', error);
      }
    }
  };

  const handleEditCampaign = async (campaign: EmailCampaign) => {
    try {
      // Carregar detalhes completos da campanha
      const response = await axios.get(`${getApiUrl()}/email-campaigns/${campaign.id}`);
      const campaignData = response.data;
      
      setSelectedCampaign(campaign);
      setNewCampaign({
        name: campaign.name,
        subject: campaign.subject,
        from_name: campaign.from_name || '',
        filters: campaignData.filters || [{ filter_type: 'crm_stage', filter_value: '' }],
      });
      
      // Se a campanha tiver template_id, carregar
      if (campaignData.template_id) {
        setSelectedTemplate(campaignData.template_id);
      }
      
      // Se tiver filtros de estágio, carregar o estágio selecionado
      if (campaignData.filters && campaignData.filters.length > 0) {
        const stageFilter = campaignData.filters.find((f: any) => f.filter_type === 'crm_stage');
        if (stageFilter) {
          setSelectedStageId(stageFilter.filter_value);
          setRecipientType('stage');
        }
      }
      
      setTabValue(0); // Ir para aba de criação/edição
    } catch (error: any) {
      console.error('Erro ao carregar campanha para edição:', error);
      // Fallback: usar dados básicos da campanha
      setSelectedCampaign(campaign);
      setNewCampaign({
        name: campaign.name,
        subject: campaign.subject,
        from_name: campaign.from_name || '',
        filters: [{ filter_type: 'crm_stage', filter_value: '' }],
      });
      setTabValue(0);
      alert(error.response?.data?.message || 'Erro ao carregar dados da campanha');
    }
  };

  const handleReprocessCampaign = async (campaignId: string) => {
    try {
      await axios.post(`${getApiUrl()}/email-campaigns/${campaignId}/start`);
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao reprocessar campanha:', error);
    }
  };

  const handleViewCampaignDetails = (campaign: EmailCampaign) => {
    setSelectedCampaign(campaign);
    setShowCampaignDetails(true);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      draft: 'default',
      scheduled: 'info',
      sending: 'primary',
      sent: 'success',
      paused: 'warning',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Rascunho',
      scheduled: 'Agendada',
      sending: 'Enviando',
      sent: 'Enviada',
      paused: 'Pausada',
      cancelled: 'Cancelada',
    };
    return labels[status] || status;
  };

  const calculateOpenRate = (campaign: EmailCampaign) => {
    if (campaign.total_sent === 0) return '0%';
    return ((campaign.total_opened / campaign.total_sent) * 100).toFixed(1) + '%';
  };

  const calculateClickRate = (campaign: EmailCampaign) => {
    if (campaign.total_sent === 0) return '0%';
    return ((campaign.total_clicked / campaign.total_sent) * 100).toFixed(1) + '%';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  const theme = useTheme();

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh', 
      p: 3,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(33, 150, 243, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(33, 150, 243, 0.06) 0%, transparent 50%)
          `,
          opacity: 0.6,
          pointerEvents: 'none'
        }}
      />

      {/* Header */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{
            fontWeight: 'bold',
            mb: 1,
            color: '#000000',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CampaignIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
            Campanhas de Email
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Sistema inteligente de email marketing para leads do CRM
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setTabValue(1)}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
              }
            }}
          >
            Nova Campanha
          </Button>
        </Box>
      </Box>

      {/* Stats Cards Elegantes */}
      <Slide direction="up" in timeout={1000}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={1200}>
              <Box className="glass-blue-card rounded-xl p-4" sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                  borderColor: 'rgba(59, 130, 246, 0.5)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Total de Campanhas
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                      {campaigns.length}
                    </Typography>
                  </Box>
                  <EmailIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.7 }} />
                </Box>
              </Box>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={1400}>
              <Box className="glass-blue-card rounded-xl p-4" sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                  borderColor: 'rgba(16, 185, 129, 0.5)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Emails Enviados
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                      {campaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: '#10b981', opacity: 0.7 }} />
                </Box>
              </Box>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={1600}>
              <Box className="glass-blue-card rounded-xl p-4" sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
                  borderColor: 'rgba(245, 158, 11, 0.5)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Taxa de Abertura
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                      {campaigns.length > 0
                        ? (
                            (campaigns.reduce((sum, c) => sum + c.total_opened, 0) /
                              campaigns.reduce((sum, c) => sum + c.total_sent, 0)) *
                            100
                          ).toFixed(1)
                        : '0'}
                      %
                    </Typography>
                  </Box>
                  <ReadIcon sx={{ fontSize: 40, color: '#f59e0b', opacity: 0.7 }} />
                </Box>
              </Box>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={1800}>
              <Box className="glass-blue-card rounded-xl p-4" sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                  borderColor: 'rgba(59, 130, 246, 0.5)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Taxa de Clique
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                      {campaigns.length > 0
                        ? (
                            (campaigns.reduce((sum, c) => sum + c.total_clicked, 0) /
                              campaigns.reduce((sum, c) => sum + c.total_sent, 0)) *
                            100
                          ).toFixed(1)
                        : '0'}
                      %
                    </Typography>
                  </Box>
                  <ClickIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.7 }} />
                </Box>
              </Box>
            </Zoom>
          </Grid>
        </Grid>
      </Slide>

      {/* Tabs */}
      <Box className="glass-blue-card rounded-xl mb-6" sx={{ 
        overflow: 'hidden'
      }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#000000',
                fontWeight: 700,
              }
            },
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
            <Tab 
              label="Minhas Campanhas" 
              icon={<CampaignIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Criar Nova Campanha" 
              icon={<AddIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Gerenciar Templates" 
              icon={<AutoAwesomeIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

      {/* Campaigns Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box className="glass-blue-card rounded-xl overflow-hidden">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  '& .MuiTableCell-head': {
                    color: '#000000',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.3)'
                  }
                }}>
                  <TableCell>Nome</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Destinatários</TableCell>
                  <TableCell align="right">Enviados</TableCell>
                  <TableCell align="right">Taxa Abertura</TableCell>
                  <TableCell align="right">Taxa Clique</TableCell>
                  <TableCell align="right">Criada em</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {campaigns.map((campaign, index) => (
                <Fade in timeout={2400 + (index * 100)} key={campaign.id}>
                  <TableRow 
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        transition: 'all 0.3s ease'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#000000' }}>
                            {campaign.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {campaign.subject}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(campaign.status)}
                      color={getStatusColor(campaign.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{(campaign.total_recipients || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {(campaign.total_sent || 0).toLocaleString()}
                    {(campaign.total_sent || 0) > 0 && (
                      <LinearProgress
                        variant="determinate"
                        value={((campaign.total_sent || 0) / (campaign.total_recipients || 1)) * 100}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', '& .MuiLinearProgress-bar': { backgroundColor: '#3b82f6' } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{calculateOpenRate(campaign)}</TableCell>
                  <TableCell align="right">{calculateClickRate(campaign)}</TableCell>
                  <TableCell align="right">
                    {new Date(campaign.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Tooltip title={campaign.status === 'sending' ? 'Campanha já está em envio' : 'Iniciar Campanha'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('🔘 Botão clicado para campanha:', campaign.id);
                              handleStartCampaign(campaign.id);
                            }}
                            disabled={campaign.status === 'sending' || startingCampaign === campaign.id || !!startingCampaign}
                            sx={{
                              background: campaign.status === 'sending' || startingCampaign === campaign.id 
                                ? 'rgba(59, 130, 246, 0.1)' 
                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: '#000000',
                              '&:hover:not(:disabled)': {
                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                transform: 'scale(1.1)',
                              },
                              '&:disabled': {
                                opacity: 0.5,
                                cursor: 'not-allowed'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {startingCampaign === campaign.id ? (
                              <CircularProgress size={16} sx={{ color: '#000000' }} />
                            ) : (
                              <PlayIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Pausar Campanha">
                        <IconButton
                          size="small"
                          onClick={() => handlePauseCampaign(campaign.id)}
                          sx={{
                            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                            color: '#000000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <PauseIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reprocessar Campanha">
                        <IconButton
                          size="small"
                          onClick={() => handleReprocessCampaign(campaign.id)}
                          sx={{
                            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                            color: '#000000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar Campanha">
                        <IconButton
                          size="small"
                          onClick={() => handleEditCampaign(campaign)}
                          sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
                            color: '#000000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver Detalhes">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewCampaignDetails(campaign)}
                          sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
                            color: '#000000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          sx={{
                            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                            color: '#000000',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </Fade>
              ))}
              {campaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Zoom in timeout={3000}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CampaignIcon sx={{ 
                          fontSize: 80, 
                          color: '#3b82f6', 
                          mb: 2,
                          opacity: 0.6
                        }} />
                        <Typography variant="h5" sx={{ 
                          color: '#3b82f6', 
                          fontWeight: 700,
                          mb: 1 
                        }}>
                          Nenhuma campanha criada ainda
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
                          Crie sua primeira campanha de email marketing
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setTabValue(1)}
                          sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
                            color: '#ffffff',
                            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                              boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                            }
                          }}
                        >
                          Criar Primeira Campanha
                        </Button>
                      </Box>
                    </Zoom>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </TabPanel>

      {/* Create Campaign Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box className="glass-blue-card rounded-xl p-4" sx={{ maxWidth: 900, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{
              p: 2,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AddIcon sx={{ color: '#ffffff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{
                fontWeight: 700,
                color: '#000000',
                mb: 1
              }}>
                Criar Nova Campanha
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                Configure sua campanha de email marketing com templates profissionais
              </Typography>
            </Box>
          </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  select
                  label="Template"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.12)',
                      },
                      '&.Mui-focused': {
                        border: '1px solid #3b82f6',
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#3b82f6',
                      }
                    },
                    '& .MuiSelect-select': {
                      color: '#000000',
                    }
                  }}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} - {template.category}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Nome da Campanha"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.12)',
                      },
                      '&.Mui-focused': {
                        border: '1px solid #3b82f6',
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#3b82f6',
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#000000',
                    }
                  }}
                />

                <TextField
                  label="Assunto do Email"
                  value={newCampaign.subject}
                  onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                  fullWidth
                  required
                  helperText="Este será o assunto que os leads verão"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.12)',
                      },
                      '&.Mui-focused': {
                        border: '1px solid #3b82f6',
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#3b82f6',
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#94a3b8',
                    }
                  }}
                />

                <TextField
                  label="Nome do Remetente"
                  value={newCampaign.from_name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, from_name: e.target.value })}
                  fullWidth
                  placeholder="Ex: Sistema CRM, Sua Empresa, etc."
                  helperText="Nome que aparecerá como remetente no email (deixe vazio para usar o padrão das configurações SMTP)"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.12)',
                      },
                      '&.Mui-focused': {
                        border: '1px solid #3b82f6',
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#3b82f6',
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#94a3b8',
                    }
                  }}
                />

                <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                  <FormLabel component="legend" sx={{ color: '#475569', mb: 1, fontWeight: 500 }}>
                    Tipo de Destinatário
                  </FormLabel>
                  <RadioGroup
                    row
                    value={recipientType}
                    onChange={(e) => {
                      setRecipientType(e.target.value as 'stage' | 'individual' | 'list');
                      // Limpar seleções ao mudar tipo
                      if (e.target.value === 'stage') {
                        setIndividualEmail('');
                        setIndividualName('');
                        setEmailList([{email: '', name: ''}]);
                        setBulkEmailInput('');
                      } else if (e.target.value === 'individual') {
                        setSelectedStageId('');
                        setEmailList([{email: '', name: ''}]);
                        setBulkEmailInput('');
                      } else {
                        setSelectedStageId('');
                        setIndividualEmail('');
                        setIndividualName('');
                      }
                    }}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: '#475569',
                      },
                      '& .MuiRadio-root': {
                        color: '#64748b',
                        '&.Mui-checked': {
                          color: '#3b82f6',
                        },
                      },
                    }}
                  >
                    <FormControlLabel value="stage" control={<Radio />} label="Estágio do CRM" />
                    <FormControlLabel value="individual" control={<Radio />} label="Email Individual" />
                    <FormControlLabel value="list" control={<Radio />} label="Lista de Emails" />
                  </RadioGroup>
                </FormControl>

                {recipientType === 'stage' ? (
                  <>
                    <TextField
                      select
                      label="Estágio do CRM"
                      value={selectedStageId}
                      onChange={(e) => setSelectedStageId(e.target.value)}
                      fullWidth
                      required
                      helperText="Selecione o estágio do CRM para enviar emails aos leads"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: 2,
                          '&:hover': {
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            background: 'rgba(59, 130, 246, 0.12)',
                          },
                          '&.Mui-focused': {
                            border: '1px solid #3b82f6',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#3b82f6',
                          }
                        },
                        '& .MuiSelect-select': {
                          color: '#000000',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#94a3b8',
                        }
                      }}
                    >
                      {crmStages.map((stage) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          {stage.name} ({stage.pipelineName})
                        </MenuItem>
                      ))}
                    </TextField>

                    {selectedStageId && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mt: 2,
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            color: '#000000',
                            fontWeight: 500
                          }
                        }}
                      >
                        A campanha será enviada para todos os leads no estágio selecionado que possuem email cadastrado
                      </Alert>
                    )}
                  </>
                ) : recipientType === 'individual' ? (
                  <>
                    <TextField
                      label="Email do Destinatário"
                      type="email"
                      value={individualEmail}
                      onChange={(e) => setIndividualEmail(e.target.value)}
                      fullWidth
                      required
                      placeholder="exemplo@email.com"
                      helperText="Digite o email do destinatário"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: 2,
                          '&:hover': {
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            background: 'rgba(59, 130, 246, 0.12)',
                          },
                          '&.Mui-focused': {
                            border: '1px solid #3b82f6',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#3b82f6',
                          }
                        },
                        '& .MuiInputBase-input': {
                          color: '#000000',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#94a3b8',
                        }
                      }}
                    />
                    <TextField
                      label="Nome do Destinatário (Opcional)"
                      value={individualName}
                      onChange={(e) => setIndividualName(e.target.value)}
                      fullWidth
                      placeholder="Nome do destinatário"
                      helperText="Nome que será usado na personalização do email"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: 2,
                          '&:hover': {
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            background: 'rgba(59, 130, 246, 0.12)',
                          },
                          '&.Mui-focused': {
                            border: '1px solid #3b82f6',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#3b82f6',
                          }
                        },
                        '& .MuiInputBase-input': {
                          color: '#000000',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#94a3b8',
                        }
                      }}
                    />

                    {individualEmail && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mt: 2,
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            color: '#000000',
                            fontWeight: 500
                          }
                        }}
                      >
                        A campanha será enviada para: {individualEmail}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontWeight: 500 }}>
                      Cole múltiplos emails de uma vez ou adicione individualmente
                    </Typography>
                    
                    <TextField
                      label="Cole múltiplos emails de uma vez"
                      multiline
                      rows={4}
                      value={bulkEmailInput}
                      placeholder="exemplo1@email.com, exemplo2@email.com&#10;ou&#10;exemplo3@email.com&#10;exemplo4@email.com"
                      helperText="Cole uma lista de emails separados por vírgula, ponto e vírgula ou quebra de linha. Clique em 'Processar Lista' para adicionar todos de uma vez."
                      fullWidth
                      onChange={(e) => setBulkEmailInput(e.target.value)}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          background: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: 2,
                          '&:hover': {
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            background: 'rgba(59, 130, 246, 0.12)',
                          },
                          '&.Mui-focused': {
                            border: '1px solid #3b82f6',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                          '&.Mui-focused': {
                            color: '#3b82f6',
                          }
                        },
                        '& .MuiInputBase-input': {
                          color: '#000000',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#94a3b8',
                        }
                      }}
                    />
                    
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        if (!bulkEmailInput.trim()) {
                          alert('Por favor, cole uma lista de emails primeiro');
                          return;
                        }
                        
                        // Processar lista de emails
                        const emails = bulkEmailInput
                          .split(/[,;\n\r]+/)
                          .map(email => email.trim())
                          .filter(email => {
                            // Validar formato básico
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return email.length > 0 && emailRegex.test(email);
                          });
                        
                        if (emails.length === 0) {
                          alert('Nenhum email válido encontrado na lista. Verifique o formato dos emails.');
                          return;
                        }
                        
                        // Adicionar emails à lista (mesclar com existentes, removendo duplicatas)
                        const existingEmails = emailList.map(item => item.email.toLowerCase());
                        const newEmails = emails
                          .filter(email => !existingEmails.includes(email.toLowerCase()))
                          .map(email => ({
                            email: email,
                            name: ''
                          }));
                        
                        if (newEmails.length === 0) {
                          alert('Todos os emails já estão na lista.');
                          setBulkEmailInput('');
                          return;
                        }
                        
                        setEmailList([...emailList, ...newEmails]);
                        setBulkEmailInput('');
                        alert(`${newEmails.length} email(s) adicionado(s) com sucesso!`);
                      }}
                      sx={{
                        mb: 2,
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        }
                      }}
                    >
                      Processar Lista de Emails
                    </Button>
                    
                    <Divider sx={{ my: 2, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                        Lista de Emails ({emailList.filter(item => item.email && item.email.trim() !== '').length} email(s))
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setEmailList([...emailList, {email: '', name: ''}]);
                        }}
                        sx={{
                          borderColor: 'rgba(59, 130, 246, 0.3)',
                          color: '#3b82f6',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            background: 'rgba(59, 130, 246, 0.1)',
                          }
                        }}
                      >
                        Adicionar Email Individual
                      </Button>
                    </Box>
                    
                    {emailList.map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                        <TextField
                          label={`Email ${index + 1}`}
                          type="email"
                          value={item.email}
                          onChange={(e) => {
                            const newList = [...emailList];
                            newList[index].email = e.target.value;
                            setEmailList(newList);
                          }}
                          fullWidth
                          required={index === 0}
                          placeholder="exemplo@email.com"
                          sx={{
                            flex: 2,
                            '& .MuiOutlinedInput-root': {
                              background: 'rgba(59, 130, 246, 0.08)',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              borderRadius: 2,
                              '&:hover': {
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                background: 'rgba(59, 130, 246, 0.12)',
                              },
                              '&.Mui-focused': {
                                border: '1px solid #3b82f6',
                                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              color: '#64748b',
                              '&.Mui-focused': {
                                color: '#3b82f6',
                              }
                            },
                            '& .MuiInputBase-input': {
                              color: '#000000',
                            }
                          }}
                        />
                        <TextField
                          label="Nome (Opcional)"
                          value={item.name}
                          onChange={(e) => {
                            const newList = [...emailList];
                            newList[index].name = e.target.value;
                            setEmailList(newList);
                          }}
                          fullWidth
                          placeholder="Nome do destinatário"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              background: 'rgba(59, 130, 246, 0.08)',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              borderRadius: 2,
                              '&:hover': {
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                background: 'rgba(59, 130, 246, 0.12)',
                              },
                              '&.Mui-focused': {
                                border: '1px solid #3b82f6',
                                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              color: '#64748b',
                              '&.Mui-focused': {
                                color: '#3b82f6',
                              }
                            },
                            '& .MuiInputBase-input': {
                              color: '#000000',
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {emailList.length > 1 && (
                            <IconButton
                              onClick={() => {
                                const newList = emailList.filter((_, i) => i !== index);
                                setEmailList(newList);
                              }}
                              sx={{
                                color: '#EF4444',
                                '&:hover': {
                                  background: 'rgba(239, 68, 68, 0.1)',
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          {index === emailList.length - 1 && (
                            <IconButton
                              onClick={() => {
                                setEmailList([...emailList, {email: '', name: ''}]);
                              }}
                              sx={{
                                color: '#10B981',
                                '&:hover': {
                                  background: 'rgba(16, 185, 129, 0.1)',
                                }
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    ))}

                    {emailList.some(item => item.email) && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mt: 2,
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: 2,
                          '& .MuiAlert-message': {
                            color: '#000000',
                            fontWeight: 500
                          }
                        }}
                      >
                        A campanha será enviada para {emailList.filter(item => item.email && item.email.trim() !== '').length} email(s) da lista
                      </Alert>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="contained"
                    onClick={handleCreateCampaign}
                    startIcon={selectedCampaign ? <EditIcon /> : <AddIcon />}
                    disabled={
                      (!selectedTemplate && !selectedCampaign) || 
                      !newCampaign.name || 
                      !newCampaign.subject || 
                      (!selectedCampaign && recipientType === 'stage' && !selectedStageId) ||
                      (!selectedCampaign && recipientType === 'individual' && !individualEmail) ||
                      (!selectedCampaign && recipientType === 'list' && emailList.filter(item => item.email && item.email.trim() !== '').length === 0)
                    }
                    sx={{
                      px: 6,
                      py: 2,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: '#ffffff',
                      boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                      borderRadius: 3,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      '&:disabled': {
                        background: 'rgba(59, 130, 246, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                        boxShadow: 'none',
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {selectedCampaign ? '💾 Salvar Alterações' : '🚀 Criar Campanha'}
                  </Button>
                </Box>
              </Box>
        </Box>
      </TabPanel>

      {/* Templates Management Tab */}
      <TabPanel value={tabValue} index={2}>
        <EmailTemplates />
      </TabPanel>

      {/* Campaign Details Modal */}
      {showCampaignDetails && selectedCampaign && (
        <Fade in timeout={800}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 100%)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={() => setShowCampaignDetails(false)}
          >
            <Zoom in timeout={1000}>
              <Box
                className="glass-blue-card rounded-xl"
                sx={{
                  maxWidth: 900,
                  maxHeight: '90vh',
                  overflow: 'auto',
                  m: 2,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        p: 2,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <AnalyticsIcon sx={{ color: '#000000', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h4" sx={{
                        fontWeight: 700,
                        color: '#ffffff',
                      }}>
                        Detalhes da Campanha
                      </Typography>
                    </Box>
                    <IconButton 
                      onClick={() => setShowCampaignDetails(false)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          color: '#ffffff',
                          background: 'rgba(59, 130, 246, 0.1)',
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                  <Box className="glass-blue-card rounded-xl p-3">
                    <Typography variant="h6" sx={{
                      fontWeight: 600,
                      color: '#3b82f6',
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <InfoIcon sx={{ fontSize: 20 }} />
                      Informações Básicas
                    </Typography>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>Nome:</Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                          {selectedCampaign.name}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>Assunto:</Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff' }}>
                          {selectedCampaign.subject}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>Status:</Typography>
                        <Chip
                          label={getStatusLabel(selectedCampaign.status)}
                          color={getStatusColor(selectedCampaign.status)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              color: '#ffffff'
                            }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>Criada em:</Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff' }}>
                          {new Date(selectedCampaign.created_at).toLocaleString('pt-BR')}
                        </Typography>
                      </Box>
                  </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                  <Box className="glass-blue-card rounded-xl p-3">
                    <Typography variant="h6" sx={{
                      fontWeight: 600,
                      color: '#10b981',
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <BarChartIcon sx={{ fontSize: 20 }} />
                      Estatísticas
                    </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box className="glass-blue-card rounded-xl" sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 700 }}>
                              {selectedCampaign.total_recipients || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              Destinatários
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box className="glass-blue-card rounded-xl" sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700 }}>
                              {selectedCampaign.total_sent || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              Enviados
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box className="glass-blue-card rounded-xl" sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                              {selectedCampaign.total_opened || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              Abertos
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box className="glass-blue-card rounded-xl" sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 700 }}>
                              {selectedCampaign.total_clicked || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              Cliques
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                  </Box>
                    </Grid>

                    <Grid item xs={12}>
                  <Box className="glass-blue-card rounded-xl p-3">
                    <Typography variant="h6" sx={{
                      fontWeight: 600,
                      color: '#f59e0b',
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <TrendingUpIcon sx={{ fontSize: 20 }} />
                      Taxa de Performance
                    </Typography>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <Box className="glass-blue-card rounded-xl" sx={{ 
                            textAlign: 'center',
                            p: 3,
                          }}>
                            <Typography variant="h3" sx={{ 
                              color: '#10b981', 
                              fontWeight: 700,
                              mb: 1
                            }}>
                              {calculateOpenRate(selectedCampaign)}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontWeight: 500
                            }}>
                              Taxa de Abertura
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box className="glass-blue-card rounded-xl" sx={{ 
                            textAlign: 'center',
                            p: 3,
                          }}>
                            <Typography variant="h3" sx={{ 
                              color: '#3b82f6', 
                              fontWeight: 700,
                              mb: 1
                            }}>
                              {calculateClickRate(selectedCampaign)}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontWeight: 500
                            }}>
                              Taxa de Clique
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                  </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Zoom>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

